// Pure view logic for Tools → Instagram ID backfill. Tested without jsdom (the
// suite sits near its per-test timeout); the page only renders what this says.

import { shouldWrite, type AccountRow, type PlanRow } from './plan.js';

/**
 * The phase-1 "can't be looked up" list, grouped by the script's own pageNote.
 * ⚠️ Grouped, never collapsed into one message — "no Zernio profile" and "the
 * profile has no Page selected" are fixed in different places by different
 * people.
 */
export function groupUnresolvable(accounts: readonly AccountRow[]): Array<{ note: string; accounts: AccountRow[] }> {
  const groups = new Map<string, AccountRow[]>();
  for (const a of accounts) {
    if (a.pageId) continue;
    // A per-profile note ("zernioProfileId X has no cached profile doc") names
    // the id; group those under one heading so the list is not one row each.
    const note = /has no cached profile doc$/.test(a.pageNote ?? '')
      ? 'linked Zernio profile has no cached profile doc'
      : (a.pageNote ?? 'no page id');
    const arr = groups.get(note) ?? [];
    arr.push(a);
    groups.set(note, arr);
  }
  return [...groups]
    .map(([note, accs]) => ({ note, accounts: [...accs].sort((x, y) => x.name.localeCompare(y.name)) }))
    .sort((a, b) => b.accounts.length - a.accounts.length);
}

/**
 * The rows an Apply would send, and what each would do. DIFFERS rows are
 * included ONLY when ticked. The server re-decides with the same
 * `shouldWrite` and re-checks the live doc, so this is a preview of the
 * decision, not the decision itself.
 */
export function rowsToApply(
  plan: readonly PlanRow[],
  tickedDiffers: ReadonlySet<string>,
): { rows: PlanRow[]; setCount: number; nameOnlyCount: number; overwriteCount: number } {
  const rows: PlanRow[] = [];
  let setCount = 0;
  let nameOnlyCount = 0;
  let overwriteCount = 0;
  for (const r of plan) {
    const overwrite = r.action === 'already set (DIFFERS!)' && tickedDiffers.has(r.id);
    const mode = shouldWrite(r, overwrite);
    if (mode === 'skip') continue;
    rows.push(r);
    if (mode === 'name-only') nameOnlyCount++;
    else if (overwrite) overwriteCount++;
    else setCount++;
  }
  return { rows, setCount, nameOnlyCount, overwriteCount };
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** "3 new, 1 corrected, 2 names topped up" — only the parts that are non-zero. */
export function applyConfirmSentence(p: { setCount: number; nameOnlyCount: number; overwriteCount: number }): string {
  const parts: string[] = [];
  if (p.setCount) parts.push(`set the Instagram account on ${p.setCount} account${p.setCount === 1 ? '' : 's'}`);
  if (p.overwriteCount) {
    parts.push(`REPLACE a different existing Instagram id on ${p.overwriteCount} account${p.overwriteCount === 1 ? '' : 's'}`);
  }
  if (p.nameOnlyCount) {
    parts.push(`fill in a missing Instagram name on ${p.nameOnlyCount} account${p.nameOnlyCount === 1 ? '' : 's'}`);
  }
  if (parts.length === 0) return 'Nothing to write.';
  return `This will ${parts.join(', ')}.`;
}
